import { WorkflowsTableTitleDecorationFilterArea } from "#src/batteries-included-components/FilterAreas/WorkflowsFilterAreas";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { WorkflowStatusToPillVariantMap } from "#src/batteries-included-components/Panels/TablePanels/WorkflowsTablePanel/WorkflowsTablePanel.helpers";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { FilterArea } from "#src/components/FilterArea";
import { FilterDrawer } from "#src/components/FilterDrawer";
import { useListWorkflows } from "#src/components/hooks/adapters/useWorkflows";
import { useCustomAttributeFiltersV2 } from "#src/components/hooks/FilterPanel/useCustomAttributeFilters";
import { DeviceUpdateStatusBanner } from "#src/components/Organization/Devices/Detail/DeviceUpdateStatusBanner";
import { EquipmentUpdateStatusBanner } from "#src/components/Organization/Facilities/Detail/Equipment/EquipmentUpdateStatusBanner";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#src/components/Redux/reducers/tableStateReducer";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { FlowUpdateStatusBanner } from "#src/routes/organization/flows/[flowId]/detail/FlowUpdateStatusBanner";
import { NetworkUpdateStatusBanner } from "#src/routes/organization/networks/[networkId]/detail/NetworkUpdateStatusBanner";
import { WorkflowDetailsRoutePath } from "#src/routes/workflows/all/[workflowId]/detail";
import { WorkflowCategoryDetailsRoutePath } from "#src/routes/workflows/categories/[workflowCategoryId]";
import { WorkflowTemplateDetailsRoutePath } from "#src/routes/workflows/templates/[workflowTemplateId]";
import { linkToAssetDetailPage } from "#src/utils/links";
import {
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableMetaHeaders,
} from "#src/utils/table";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import {
  Accordion,
  Button,
  DataTable,
  DataTablePanel,
  DateSelectorInput,
  Dialog,
  DropdownInput,
  getDataTableGroupByKeysForGroupByData,
  Icon,
  Pill,
  TextInput,
  useToast,
  type HeaderType,
} from "@validereinc/common-components";
import {
  isResponseErrorType,
  Resources,
  SortDirection,
  WorkflowAdapter,
  WorkflowCategoriesAdapter,
  WorkflowSchema,
  WorkflowStatus,
  WorkflowTemplatesAdapter,
  type AssetTypeType,
  type GroupedByEntryType,
  type ResponseErrorType,
  type WorkflowType,
} from "@validereinc/domain";
import {
  HTTPForbiddenError,
  HTTPNotFoundError,
  HTTPResponseError,
  toTitleCaseString,
} from "@validereinc/utilities";
import endOfMonth from "date-fns/endOfMonth";
import subMonths from "date-fns/subMonths";
import pLimit from "p-limit";
import React, { useMemo, useState } from "react";
import { z } from "zod";
import { getAssetFilters } from "./AssociatedWorkflows.helpers";
import { FacilityUpdateStatusBanner } from "./FacilityUpdateStatusBanner";

export const AssociatedWorkflowsTableFilterSchema = z
  .object({
    workflows: z.record(z.any()),
  })
  .merge(
    WorkflowSchema.pick({
      name: true,
      workflow_template_id: true,
    })
  )
  .partial();
export type AssociatedWorkflowsTableFilterType = z.infer<
  typeof AssociatedWorkflowsTableFilterSchema
>;
const AssociatedWorkflowsTableFilterKeys =
  AssociatedWorkflowsTableFilterSchema.keyof().Enum;
export const AssociatedWorkflowsViewFilterSchema = z
  .object({
    date_range: z.object({
      from: z.string().datetime(),
      to: z.string().datetime().optional(),
    }),
    status: z.array(z.union([z.literal(""), z.nativeEnum(WorkflowStatus)])),
    workflow_category_id: z.string(),
  })
  .merge(
    WorkflowSchema.pick({
      workflow_template_id: true,
    })
  )
  .partial();
const AssociatedWorkflowsViewFilterKeys =
  AssociatedWorkflowsViewFilterSchema.keyof().Enum;
export type AssociatedWorkflowsViewFilterType = z.infer<
  typeof AssociatedWorkflowsViewFilterSchema
>;

export const AssociatedWorkflowsTab = ({
  resourceId,
  resourceType,
  resourceName,
}: {
  resourceId: string;
  resourceType: AssetTypeType;
  resourceName?: string;
}) => {
  const { toast } = useToast();
  const [
    tableConfigStorageKey,
    viewConfigStorageKey,
    filterConfigStorageKey,
    tableStateStorageKey,
    groupByTablesStateStorageKey,
  ] = useStorageKey(
    [
      "table-configuration",
      "view-configuration",
      "filter-configuration",
      "table-state",
      "group-by-tables-state",
    ],
    `${resourceType}-${resourceId}-workflows`
  );
  const { localize, isLoading: isLoadingLocalization } = useLocalization();
  const queryClient = useQueryClient();
  const metaHeaders = useDataTableMetaHeaders();
  const [selectedRows, setSelectedRows] = useState({});
  const [tableFilters] = useStickyState<AssociatedWorkflowsTableFilterType>(
    {},
    filterConfigStorageKey
  );
  const [viewFilters] = useStickyState<AssociatedWorkflowsViewFilterType>(
    {},
    viewConfigStorageKey
  );
  const {
    [AssociatedWorkflowsViewFilterKeys.date_range]: dateRange,
    [AssociatedWorkflowsViewFilterKeys.status]: status,
    [AssociatedWorkflowsViewFilterKeys.workflow_category_id]: categoryId,
    [AssociatedWorkflowsViewFilterKeys.workflow_template_id]: templateId,
  } = viewFilters;
  const { [AssociatedWorkflowsTableFilterKeys.name]: name } = tableFilters;

  const filters = useMemo(
    () => ({
      ...getAssetFilters(resourceType, {
        assetId: resourceId,
        assetName: resourceName,
      }),
      created_at: dateRange,
      ...(status ? { status } : {}),
      "workflow_category.id": categoryId,
      workflow_template_id: templateId,
      ...(name ? { name } : {}),
    }),
    [dateRange, status, categoryId, templateId, name, tableFilters, viewFilters]
  );

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const reqPayload: Parameters<typeof WorkflowAdapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters,
  };
  const { data, isLoading, isFetching } = useListWorkflows(reqPayload);
  const items = data?.data ?? [];

  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...reqPayload,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map((key) => groupByTablesStates.get(key) ?? reqPayload)
  );

  const workflowsGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: [Resources.WORKFLOW, "getList", d.payload],
      queryFn: () => WorkflowAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const attributeFilters = useCustomAttributeFiltersV2({
    assetType: resourceType,
    prefix: `workflow.${resourceType}.custom_attributes`,
  });

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const deleteMutation = useMutation({
    mutationFn: (params: Parameters<typeof WorkflowAdapter.deleteOne>[0]) => {
      return WorkflowAdapter.deleteOne(params);
    },
  });

  const headers: Array<HeaderType<WorkflowType>> = useMemo(
    () => [
      {
        label: "Name",
        key: "id",
        isSortable: false,
        renderComponent: ({ item }: { item: WorkflowType }) => {
          return (
            <RoutingLink
              to={WorkflowDetailsRoutePath.toLinkParts({
                pathParams: {
                  workflowId: item.id,
                },
              })}
            >
              {item.name}
            </RoutingLink>
          );
        },
      },
      {
        label: "Status",
        key: "status",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <Pill
            variant={WorkflowStatusToPillVariantMap[item.status] || "default"}
          >
            {toTitleCaseString(item.status, {
              intent: "ui",
              exceptions: ["oil-and-gas"],
            })}
          </Pill>
        ),
      },
      {
        label: "Due Date",
        key: "due_date",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell
            value={item.due_date}
            convertToUTC={false}
          />
        ),
      },
      {
        label: "Asset",
        isSortable: false,
        key: "asset.name",
        renderComponent: ({ item }: { item: WorkflowType }) =>
          item.asset ? (
            <RoutingLink
              to={linkToAssetDetailPage(item.asset.asset_type, item.asset.id)}
            >
              {item.asset.name}
            </RoutingLink>
          ) : (
            "-"
          ),
      },
      {
        label: "Category",
        key: "workflow_template.workflow_category.name",
        isSortable: false,
        renderComponent: ({ item }: { item: WorkflowType }) => (
          <RoutingLink
            to={WorkflowCategoryDetailsRoutePath.toLinkParts({
              pathParams: {
                workflowCategoryId: item.workflow_template.workflow_category.id,
              },
            })}
          >
            {item.workflow_template.workflow_category.name}
          </RoutingLink>
        ),
      },
      {
        label: "Template",
        key: "workflow_template_id",
        isSortable: false,
        isGroupable: true,
        renderComponent: ({ item }: { item: WorkflowType }) =>
          item.workflow_template?.name ? (
            <RoutingLink
              to={WorkflowTemplateDetailsRoutePath.toLinkParts({
                pathParams: {
                  workflowTemplateId: item.workflow_template_id,
                },
              })}
            >
              {item.workflow_template.name}
            </RoutingLink>
          ) : (
            "-"
          ),
      },
      ...metaHeaders,
    ],
    [attributeFilters.customAttributeFilters, metaHeaders]
  );

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: workflowsGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemId: (item) => item.id,
    totalSelectableItems,
  });

  return (
    <>
      <FlowUpdateStatusBanner />
      <EquipmentUpdateStatusBanner />
      <FacilityUpdateStatusBanner />
      <DeviceUpdateStatusBanner />
      <NetworkUpdateStatusBanner />
      <FilterArea.Root
        storageKey={viewConfigStorageKey}
        defaultValues={{
          date_range: {
            from: subMonths(new Date(), 6),
            to: endOfMonth(new Date()),
          },
        }}
      >
        <FilterArea.Container aria-label="View filters for Workflows">
          <FilterArea.Content>
            {({ handleOnChange }) => (
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <DateSelectorInput
                  name={AssociatedWorkflowsViewFilterKeys.date_range}
                  variant="month"
                  isRange
                  isInline
                  label="Created At"
                  description="Filter by Created At"
                  isOptionalTextShown={false}
                  isLabelShown={false}
                  onChange={(val) =>
                    handleOnChange(
                      val,
                      AssociatedWorkflowsViewFilterKeys.date_range
                    )
                  }
                />
                <DropdownInput
                  name={AssociatedWorkflowsViewFilterKeys.workflow_category_id}
                  onFetchData={async (payload) => {
                    let { data } = await WorkflowCategoriesAdapter.getList({
                      ...payload,
                      filters: {
                        ...(payload.searchTerm
                          ? {
                              name: payload.searchTerm,
                            }
                          : {}),
                      },
                    });

                    // REVIEW: not a fan of the fact that we need to do this. A refactor of DropdownInput is needed.
                    if (Array.isArray(payload.value)) {
                      data = data.filter((d) => payload.value.includes(d.id));
                    }

                    return data;
                  }}
                  labelKey="name"
                  valueKey="id"
                  placeholder="Select Category..."
                  label="Workflow Category"
                  description="Filter by Workflow Category"
                  isOptionalTextShown={false}
                  isLabelShown={false}
                  isInline
                  onChange={(val) =>
                    handleOnChange(
                      val,
                      AssociatedWorkflowsViewFilterKeys.workflow_category_id
                    )
                  }
                />
                <DropdownInput
                  name={AssociatedWorkflowsViewFilterKeys.workflow_template_id}
                  onFetchData={async (payload) => {
                    let { data } = await WorkflowTemplatesAdapter.getList({
                      ...payload,
                      filters: {
                        ...(payload.searchTerm
                          ? {
                              name: payload.searchTerm,
                            }
                          : {}),
                      },
                    });

                    // REVIEW: not a fan of the fact that we need to do this. A refactor of DropdownInput is needed.
                    if (Array.isArray(payload.value)) {
                      data = data.filter((d) => payload.value.includes(d.id));
                    }

                    return data;
                  }}
                  labelKey="name"
                  valueKey="id"
                  placeholder="Select Template..."
                  label="Workflow Template"
                  description="Filter by Workflow Template"
                  isOptionalTextShown={false}
                  isLabelShown={false}
                  isMulti
                  isInline
                  onChange={(val) =>
                    handleOnChange(
                      val,
                      AssociatedWorkflowsViewFilterKeys.workflow_template_id
                    )
                  }
                />
              </div>
            )}
          </FilterArea.Content>
        </FilterArea.Container>
      </FilterArea.Root>
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          title: "Workflows",
          isFluidY: false,
          titleDecorator: (
            <WorkflowsTableTitleDecorationFilterArea
              filterConfigStorageKey={viewConfigStorageKey}
            />
          ),
          loaded: !isLoadingLocalization,
        }}
        filterComponent={
          <FilterArea.Root
            storageKey={filterConfigStorageKey}
            applyDefaultValues
          >
            <FilterArea.Container aria-label="Filters for Workflows">
              <FilterDrawer.Root>
                <FilterArea.Content>
                  {({ handleOnChange }) => (
                    <div style={{ marginRight: 8, marginBottom: 0 }}>
                      <TextInput
                        name={AssociatedWorkflowsTableFilterKeys.name}
                        type="search"
                        placeholder="Search..."
                        label="Search"
                        isLabelShown={false}
                        isInline
                        onChange={(val) =>
                          handleOnChange(
                            val,
                            AssociatedWorkflowsTableFilterKeys.name
                          )
                        }
                      />
                    </div>
                  )}
                </FilterArea.Content>
                <FilterDrawer.Trigger />
                <FilterDrawer.Content>
                  <Accordion defaultActiveKeys={[resourceType]}>
                    <Accordion.AccordionPanel
                      dataKey={resourceType}
                      title={localize(`${resourceType}_plural`)}
                      isLoading={attributeFilters.isLoading}
                    >
                      {...attributeFilters.customAttributeFilters}
                    </Accordion.AccordionPanel>
                  </Accordion>
                </FilterDrawer.Content>
              </FilterDrawer.Root>
            </FilterArea.Container>
          </FilterArea.Root>
        }
        dataTableProps={{
          intent: "simplicity-first",
          headers,
          items: data?.data ?? [],
          isLoading,
          isBusy: isFetching || deleteMutation.isLoading,
          getItemId: (item) => item.id,
          getItemBulkActions: ({ buttonProps }) => [
            <IsFeatureAvailable
              key="delete"
              featureFlagQuery="core:workflows"
              permissionQuery="workflows:delete"
              fallbackChildren={null}
            >
              <Button
                {...buttonProps}
                variant="error-outline"
                slotLeft={<Icon variant="trash" />}
                onClick={() => setIsDeleteConfirmationOpen(true)}
              >
                Delete
              </Button>
            </IsFeatureAvailable>,
          ],
          selected: selectedRows,
          onSelectionChange: (newSelected) => setSelectedRows(newSelected),
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: data?.total_entries,
          },
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
        }}
        {...groupByDataTableProps}
      />
      <Dialog
        title="Delete workflow(s)?"
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        actionRow={[
          <Button
            key="submit"
            variant="error"
            isLoading={deleteMutation.isLoading}
            onClick={() => {
              const selectedRowIds = Object.keys(selectedRows);
              const limitPromises = pLimit(8);

              setIsDeleteConfirmationOpen(false);
              toast
                .pushAsync(
                  Promise.all(
                    selectedRowIds.map((id) =>
                      limitPromises(async () =>
                        deleteMutation.mutateAsync({
                          id,
                          previousData: selectedRows[id],
                        })
                      )
                    )
                  ),
                  {
                    intent: "note",
                    description: `Deleting ${selectedRowIds.length} selected workflows...`,
                    isBusy: deleteMutation.isLoading,
                  },
                  {
                    isDismissible: false,
                    duration: Infinity,
                  }
                )
                .then(({ data, toast: firstToast }) => {
                  toast.push(
                    {
                      intent: "success",
                      description: `Successfully deleted ${data.length} workflow(s)`,
                    },
                    {
                      id: firstToast.id,
                      isDismissible: true,
                      duration: 5000,
                    }
                  );
                  setSelectedRows({});
                })
                .catch(({ error, toastId }) => {
                  const isNotFoundError = error instanceof HTTPNotFoundError;
                  const isForbiddenError = error instanceof HTTPForbiddenError;
                  const isNodeAPIError =
                    error instanceof HTTPResponseError &&
                    isResponseErrorType(error.responseBodyJson);

                  toast.push(
                    {
                      intent: "error",
                      description: isNodeAPIError
                        ? `Failed to delete selected workflows: ${(error.responseBodyJson as unknown as ResponseErrorType | undefined)?.error}`
                        : isNotFoundError || isForbiddenError
                          ? `Failed to delete selected workflows: ${error.getExplanation()}`
                          : "Failed to delete workflows.",
                    },
                    {
                      id: toastId,
                      isDismissible: true,
                      duration: 5000,
                    }
                  );
                })
                .finally(() => {
                  queryClient.invalidateQueries({
                    queryKey: [Resources.WORKFLOW],
                  });
                });
            }}
          >
            Delete
          </Button>,
        ]}
      >
        You&apos;re deleting {Object.keys(selectedRows).length} workflow(s).
        This action cannot be undone. Are you sure you want to continue?
      </Dialog>
    </>
  );
};
